import GraphXAxisLabelItem from 'components/atoms/GraphXaxisLabelItem';
import Barchart from 'components/molecules/Barchart';
import HorizontalInfiniteScroller from 'components/molecules/HorizontalInfiniteScroller';
import Legend from 'components/molecules/Legend';
import {
    barWidth,
    legendData,
    salesGraphWidth,
} from 'components/organism/GrowthAnalysis/SalesChartContainer/SalesChartContainer';
import { yAxisGraphWidth } from 'components/template/CommercialForecast/CFExpandedView';
import { height } from 'components/template/GrowthAnalysis/ExpandedView';
import { UserContext } from 'contexts/ConfigContext';
import { PricingAnalyticsDataContext } from 'contexts/PricingAnalyticsContext';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    fetchCurrency,
    filterOutDisplayOnlyAttributes,
    formatNumWithKMBAlt,
    getConfig,
    scaleMaxValue,
    scaleMinValue,
} from 'utils/helperMethods';
import LensConfigManger from '../LensConfigManager/LensConfigManger';
import { LenseConfig, PricingReport } from 'types/pricingSimulator';
import { FilterContextData } from 'contexts/FilterContext';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { pricingAnalyticsUrls } from 'config/urls';
import Loader from 'components/molecules/Loader';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import { changeAnalyticsChartParser } from 'parser/pricingAnalytics/changeAnalyticsParser';
import { ConfigKeys } from 'utils/Enums';

const mainHeader = 'Incremental Decremental Change';
const containerId = 'change-analytics-chart-container';
const ChangeAnalyticsContainer: FC = () => {
    const containerWidth = useContainerWidth({
        containerId: containerId,
        buffer: yAxisGraphWidth + 34 * 2 + 112,
    }); //subtracting graph width and padding on 2 sides

    //inf scroller width ref
    const infScrollerWidthRef = useRef<any[]>([0, 0]);

    //filter context
    const { allDimAndAttributeTypeList, timePeriodSelection, timePeriodLoading } = useContext(FilterContextData);
    const enabledVariable = Boolean(
        allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !timePeriodLoading &&
            timePeriodSelection,
    );
    const {
        selectedReportInfo,
        updateReportProperty,
        filters,
        isUpdate,
        loggedInUser,
        setSelectedReportInfo,
        showPercentageChange,
    } = useContext(PricingAnalyticsDataContext);
    const { appConfig } = useContext(UserContext);
    const pricingSimulatorConfig = getConfig('pricingSimulator', appConfig);
    const analyticsChartLimit = pricingSimulatorConfig.analyticsChartLimit;
    const commonConfig = getConfig(ConfigKeys.COMMON_CONFIG, appConfig);
    const { decimalForNonPercentage, decimalForPercentage } = commonConfig;
    const [commonVariables, setCommonVariables] = useState<any[] | undefined>();

    //set variables
    useEffect(() => {
        //reset inf scroller width
        infScrollerWidthRef.current = [0, 0];
        const variables = selectedReportInfo?.changeAnalytics?.input?.map((graphItem) => {
            return {
                kpi_id: graphItem?.kpiId,
                graph_id: graphItem?.graphId,
                attribute_type: graphItem?.attributeType?.value,
                user_name: loggedInUser,
                base_scenario_id: selectedReportInfo?.baseScenario?.scenarioId,
                report_id: selectedReportInfo?.reportId,
                filters,
                is_update: isUpdate,
                limit: analyticsChartLimit,
                offset: 0,
                show_percent: showPercentageChange,
            };
        });
        setCommonVariables(variables);
    }, [
        loggedInUser,
        filters,
        selectedReportInfo?.baseScenario?.scenarioId,
        selectedReportInfo?.changeAnalytics?.input,
        showPercentageChange,
    ]);

    //chart 1
    const { loading: chart1Loading, error: chart1Error } = useCommonApiHandler({
        method: 'post',
        queryConfig: { enabled: Boolean(enabledVariable) && Boolean(commonVariables?.[0]?.kpi_id) },
        url: pricingAnalyticsUrls.getChangeAnalyticsDataUrl,
        queryKey: ['pa-change-analytics-chart', 'pa-change-analytics-chart-1', commonVariables?.[0]],
        variables: { ...(commonVariables?.[0] || {}) },
        parserFunction: (res: any) => {
            if (res?.error || !res?.data?.length) {
                throw 'No Data';
            }
            const outputObj = changeAnalyticsChartParser(
                Number(commonVariables?.[0]?.graph_id),
                res?.total_count,
                res?.data,
            );

            //if inf scroller , then merge data
            if (commonVariables?.[0]?.offset > 0) {
                outputObj.maxVal = Math.max(
                    selectedReportInfo?.changeAnalytics?.output?.[0]?.maxVal ?? 0,
                    outputObj.maxVal ?? 0,
                );
                outputObj.minVal = Math.min(
                    selectedReportInfo?.changeAnalytics?.output?.[0]?.minVal ?? 0,
                    outputObj.minVal ?? 0,
                );
                outputObj.data = [
                    ...(selectedReportInfo?.changeAnalytics?.output?.[0]?.data ?? []),
                    ...outputObj?.data,
                ];
            }
            setSelectedReportInfo((prevState: any): PricingReport => {
                return {
                    ...prevState,
                    changeAnalytics: {
                        input: prevState?.changeAnalytics?.input,
                        output: [outputObj, prevState?.changeAnalytics?.output?.[1]],
                    },
                };
            });
            return {};
        },
    });

    //chart 2
    const { loading: chart2Loading, error: chart2Error } = useCommonApiHandler({
        method: 'post',
        url: pricingAnalyticsUrls.getChangeAnalyticsDataUrl,
        queryConfig: { enabled: Boolean(enabledVariable) && Boolean(commonVariables?.[1]?.kpi_id) },
        queryKey: ['pa-change-analytics-chart', 'pa-change-analytics-chart-2', commonVariables?.[1]],
        variables: { ...(commonVariables?.[1] || {}) },
        parserFunction: (res: any) => {
            if (res?.error || !res?.data?.length) {
                throw 'No Data';
            }
            const outputObj = changeAnalyticsChartParser(
                Number(commonVariables?.[1]?.graph_id),
                res?.total_count,
                res?.data,
            );

            //if inf scroller , then merge data
            if (commonVariables?.[1]?.offset > 0) {
                outputObj.maxVal = Math.max(
                    selectedReportInfo?.changeAnalytics?.output?.[1]?.maxVal ?? 0,
                    outputObj.maxVal ?? 0,
                );
                outputObj.minVal = Math.min(
                    selectedReportInfo?.changeAnalytics?.output?.[1]?.minVal ?? 0,
                    outputObj.minVal ?? 0,
                );
                outputObj.data = [
                    ...(selectedReportInfo?.changeAnalytics?.output?.[1]?.data ?? []),
                    ...outputObj?.data,
                ];
            }

            setSelectedReportInfo((prevState: any): PricingReport => {
                return {
                    ...prevState,
                    changeAnalytics: {
                        input: prevState?.changeAnalytics?.input,
                        output: [prevState?.changeAnalytics?.output?.[0], outputObj],
                    },
                };
            });
            return {};
        },
    });

    const indexLoaderErrorMapping: any = {
        0: [chart1Loading, chart1Error],
        1: [chart2Loading, chart2Error],
    };

    const infiniteScrollOnNext = useCallback(
        (width: number, loading: boolean, index: number) => {
            if (!loading) {
                const newOffset = Number(commonVariables?.[index]?.offset) + analyticsChartLimit;

                //updating width
                infScrollerWidthRef.current.splice(index, 1, width);

                //updating offset
                setCommonVariables((prevValue: any) => {
                    return prevValue?.map((variable: any, idx: number) => {
                        return {
                            ...variable,
                            offset: index === idx ? newOffset : variable?.offset,
                        };
                    });
                });
            }
        },
        [commonVariables, setCommonVariables],
    );

    const selectedLensItem = useMemo(() => {
        const names: any[] = [];
        selectedReportInfo?.changeAnalytics?.input?.forEach((item, index) => {
            item?.lensConfig?.forEach((lens) => {
                lens?.attributes?.forEach((kpi) => {
                    if (kpi.isSelected && !names?.[index]) {
                        names.push(kpi);
                        return;
                    }
                    if (names?.[index]) return;
                });
                if (names?.[index]) return;
            });
            if (names?.[index]) return;
        });
        return names;
    }, [selectedReportInfo?.changeAnalytics?.input]);
    return (
        <div id={containerId} className="w-full grid grid-cols-2 gap-4 mt-4 ">
            {selectedReportInfo?.changeAnalytics?.output?.map((dataItem, index) => {
                const graphViewContainerWidth = Math.max(
                    dataItem?.data ? dataItem.data?.length * salesGraphWidth : 0,
                    containerWidth / 2,
                );

                const perGraphComponentWidth = graphViewContainerWidth / dataItem?.data?.length || 0;

                return (
                    <div key={dataItem?.id} className="p-4 border border-backgroundGreyAlt1 rounded-lg">
                        {indexLoaderErrorMapping?.[index]?.[0] ? (
                            <div style={{ height }}>
                                <Loader />
                            </div>
                        ) : (
                            <>
                                <div className="flex justify-between items-center">
                                    <p className="text-subHeaderColor font-sm font-medium">{mainHeader}</p>
                                    <div className="flex gap-2 items-center">
                                        <LensConfigManger
                                            placeHolderLabel="Search KPI..."
                                            showAttributePanelToTheRight={index === 0}
                                            buttonName={selectedLensItem?.[index]?.name}
                                            initialConfigDetails={
                                                filterOutDisplayOnlyAttributes(
                                                    selectedReportInfo?.changeAnalytics?.input?.[index]?.lensConfig ||
                                                        [],
                                                ) || []
                                            }
                                            onSubmitHandler={(updatedDetails: LenseConfig[]) => {
                                                let kpiId: any = null;
                                                updatedDetails?.forEach((lens) => {
                                                    lens?.attributes?.forEach((kpi) => {
                                                        if (kpi?.isSelected) {
                                                            kpiId = kpi?.attributeId;
                                                            return;
                                                        }
                                                        if (kpiId) return;
                                                    });
                                                    if (kpiId) return;
                                                });
                                                //update lensconfig and kpi id
                                                const tempInput = selectedReportInfo?.changeAnalytics?.input?.map(
                                                    (graphItem, graphIndex) => {
                                                        const isGraphItem = graphIndex === index;
                                                        return {
                                                            ...graphItem,
                                                            kpiId: isGraphItem ? kpiId : graphItem?.kpiId,
                                                            lensConfig: isGraphItem
                                                                ? updatedDetails
                                                                : graphItem?.lensConfig,
                                                        };
                                                    },
                                                );
                                                updateReportProperty('changeAnalytics', {
                                                    output: selectedReportInfo?.changeAnalytics?.output,
                                                    input: tempInput,
                                                });
                                            }}
                                            isMultiSelect={false}
                                            outputCallLoading={false}
                                            isLoading={false}
                                            saveButtonLabel="Save Changes"
                                            addAlignmentFix={index === 1}
                                            scrollContainerMaxHeight={250}
                                        />
                                    </div>
                                </div>
                                {indexLoaderErrorMapping?.[index]?.[1] ? (
                                    <div
                                        className="flex items-center justify-center"
                                        style={{ height: `calc(100% - 100px)` }}
                                    >
                                        <ErrorBox msg={String(indexLoaderErrorMapping?.[index]?.[1])} />
                                    </div>
                                ) : (
                                    <>
                                        {/* LEGEND WRAPPER */}
                                        <div className="flex justify-between my-4">
                                            <p className="w-36 font-xs text-lightBlack">
                                                {selectedLensItem?.[index]?.name}
                                            </p>
                                            <Legend data={legendData} />
                                            <p className="w-22"></p>
                                        </div>
                                        <div id={`change-analytics-${dataItem.id}`} className="flex ml-1 w-full">
                                            {/* Y AXIS */}
                                            <div style={{ minWidth: yAxisGraphWidth }}>
                                                <Barchart
                                                    colorKey="color"
                                                    dataset={[]}
                                                    height={height}
                                                    loading={false}
                                                    chartContainer={`SalesY-${dataItem.id}`}
                                                    fields={['category', 'sales']}
                                                    minBound={scaleMinValue(80, dataItem?.minVal)}
                                                    maxBound={scaleMaxValue(80, dataItem?.maxVal)}
                                                    width={yAxisGraphWidth}
                                                    YGrid={false}
                                                    padding={[0, 0, 0, 0]}
                                                    yAxisLabelFormatter={(val: any) => {
                                                        return formatNumWithKMBAlt(
                                                            val,
                                                            showPercentageChange
                                                                ? decimalForPercentage
                                                                : decimalForNonPercentage,
                                                            showPercentageChange
                                                                ? decimalForPercentage
                                                                : decimalForNonPercentage,
                                                        );
                                                    }}
                                                    xAxisConfig={false}
                                                />
                                            </div>
                                            {/* MAIN AXIS */}
                                            <HorizontalInfiniteScroller
                                                id={`change-analytics-infinite-scroll${dataItem.id}`}
                                                hasMore={dataItem?.data?.length < dataItem.count || false}
                                                scrollThreshold={0.9}
                                                toScrollWidth={infScrollerWidthRef.current?.[index] ?? 0}
                                                onNext={(width: number) => {
                                                    infiniteScrollOnNext(
                                                        width,
                                                        indexLoaderErrorMapping?.[index]?.[0],
                                                        index,
                                                    );
                                                }}
                                            >
                                                <div
                                                    id={`change-analytics-infinite-scroll${dataItem.id}`}
                                                    className="flex pb-[45px]"
                                                    style={{ maxWidth: '100%', overflowX: 'auto' }}
                                                >
                                                    <div>
                                                        <div style={{ height: height }}>
                                                            <Barchart
                                                                barWidth={barWidth}
                                                                //yAxisConfig={false}
                                                                dataset={dataItem?.data || []}
                                                                height={height}
                                                                loading={false}
                                                                chartContainer={`SalesX-${dataItem.id}`}
                                                                fields={['category', 'sales']}
                                                                intervalLabelTooltipKey={'labelSales'}
                                                                minBound={scaleMinValue(80, dataItem?.minVal)}
                                                                maxBound={scaleMaxValue(80, dataItem?.maxVal)}
                                                                width={graphViewContainerWidth || salesGraphWidth}
                                                                intervalFormatter={(val) => {
                                                                    return `${
                                                                        showPercentageChange
                                                                            ? ''
                                                                            : selectedLensItem?.[index]?.pre ?? ''
                                                                    }${formatNumWithKMBAlt(
                                                                        val,
                                                                        showPercentageChange
                                                                            ? decimalForPercentage
                                                                            : decimalForNonPercentage,
                                                                        showPercentageChange
                                                                            ? decimalForPercentage
                                                                            : decimalForNonPercentage,
                                                                    )}${showPercentageChange ? '%' : ''}`;
                                                                }}
                                                                tooltipFormatter={(xAxis, val) => {
                                                                    const tooltipVal = `${
                                                                        showPercentageChange
                                                                            ? ''
                                                                            : selectedLensItem?.[index]?.pre ?? ''
                                                                    }${formatNumWithKMBAlt(
                                                                        val,
                                                                        showPercentageChange
                                                                            ? decimalForPercentage
                                                                            : decimalForNonPercentage,
                                                                        showPercentageChange
                                                                            ? decimalForPercentage
                                                                            : decimalForNonPercentage,
                                                                    )}${showPercentageChange ? '%' : ''}`;
                                                                    return {
                                                                        name: xAxis,
                                                                        value: tooltipVal,
                                                                    };
                                                                }}
                                                                colorKey={'color'}
                                                            />
                                                        </div>
                                                        {/* X AXIS ITEM LABEL */}
                                                        <div className="flex items-center">
                                                            {dataItem?.data?.map((item: any) => {
                                                                return (
                                                                    <GraphXAxisLabelItem
                                                                        width={
                                                                            perGraphComponentWidth || salesGraphWidth
                                                                        }
                                                                        item={item}
                                                                    />
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                </div>
                                            </HorizontalInfiniteScroller>
                                        </div>
                                        <p className="text-lightBlack font-xs relative top-[-40px] left-[25px] flex justify-center">
                                            {selectedReportInfo?.changeAnalytics?.input?.[index]?.attributeType?.label}
                                        </p>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default ChangeAnalyticsContainer;
